import { useQueries } from "@tanstack/react-query";
import { workflowInvitationQueryOptions } from "@/lib/data/react-query/connections/workflow-invitations";
import type { MatterSignatureRequestListItemV2 } from "@/lib/data/requests/signature-requests/signature-request";
import {
  Card,
  CardHeader,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from "@/components/ui";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import {
  MatterSignatureRequestItem,
  type MatterSignatureRequestListItem,
} from "./MatterSignatureRequestItem";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter } from "@/lib/types/Matter";
import { FileSignatureRequestIcon } from "@/lib/utils/iconUtils";
import dayjs from "dayjs";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import {
  displaySignatureRequestUserSignerName,
  mapSignatureRequestV2ToListItem,
} from "@/lib/utils/matters/signature-request-report-utils";
import { formatContactsName } from "@/lib/utils/format";
import { buildWorkflowContactConnectionPresentations } from "@/lib/utils/connections/workflow-contact-connections";

type SignatureRequestStatusTab = "outstanding" | "expired" | "completed";
type SignatureRequestFilterTab = SignatureRequestStatusTab | "all";

const getStatusTab = (
  signatureRequest: MatterSignatureRequestListItem,
): SignatureRequestStatusTab => {
  const contactSigners = signatureRequest.matterSignatureRequestContactSigners;
  const userSigners = signatureRequest.matterSignatureRequestUserSigners;

  if (contactSigners.length === 0 && userSigners.length === 0) {
    return "outstanding";
  }

  const allSigned =
    contactSigners.every((signer) => !!signer.signatureRequestComplete) &&
    userSigners.every((signer) => !!signer.signatureRequestComplete);

  if (allSigned) {
    return "completed";
  }

  const expirationDate = signatureRequest.matterSignatureRequestExpirationDate;
  const hasExpired =
    Boolean(expirationDate) && dayjs().isAfter(dayjs(expirationDate));
  if (hasExpired) {
    return "expired";
  }

  return "outstanding";
};

const getStatusLabel = (
  signatureRequest: MatterSignatureRequestListItem,
): "Completed" | "Expired" | "Pending" | "No Signers" => {
  const statusTab = getStatusTab(signatureRequest);
  if (statusTab === "completed") return "Completed";
  if (statusTab === "expired") return "Expired";
  return "Pending";
};

interface SignatureRequestsProps {
  matter: Matter;
}

export const SignatureRequests = ({ matter }: SignatureRequestsProps) => {
  const { hasPagePermission, hasActionPermission } = useUser();
  const hasViewPermission = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailSignatureRequests,
  );
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterSignatureRequest,
    ACTION_PERMISSION_VALUES.create,
  );
  const { openCreateSignatureRequestModal } = useGlobalModal();
  const [activeTab, setActiveTab] = useState<SignatureRequestFilterTab>("all");
  const [query, setQuery] = useState("");
  const signatureRequests =
    matter.signatureRequests as unknown as MatterSignatureRequestListItemV2[];

  const invitationResults = useQueries({
    queries: signatureRequests.map((request) =>
      workflowInvitationQueryOptions(
        `matters/v4/signature-requests/${request.id}`,
        matter.id,
      ),
    ),
  });
  const signatureRequestData = useMemo(() => {
    return [...signatureRequests]
      .map((signatureRequest, index) => {
        const normalizedSignatureRequest = mapSignatureRequestV2ToListItem({
          ...signatureRequest,
          invitations: invitationResults[index]?.data,
        });
        const contactSigners = buildWorkflowContactConnectionPresentations({
          invitations: invitationResults[index]?.data,
          connections: signatureRequest.connections,
          label: "Signer",
          matterId: matter.id,
          sourceObjectType: "matter_signature_request",
        });
        return {
          ...normalizedSignatureRequest,
          matterSignatureRequestContactSigners: contactSigners,
        };
      })
      .sort(
        (a, b) =>
          new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime(),
      );
  }, [matter.id, signatureRequests, invitationResults]);

  const visibleRows = useMemo(() => {
    const rows =
      activeTab === "all"
        ? signatureRequestData
        : signatureRequestData.filter((signatureRequest) => {
            return getStatusTab(signatureRequest) === activeTab;
          });

    const sanitizedQuery = query.trim();
    if (!sanitizedQuery) {
      return rows;
    }

    const searchable = rows.map((signatureRequest) => ({
      signatureRequest,
      matterFileName: signatureRequest.matterFileName,
      statusLabel: getStatusLabel(signatureRequest),
      contactSignerNames: signatureRequest.matterSignatureRequestContactSigners
        .map((signer) => formatContactsName(signer.contact))
        .filter(Boolean)
        .join(" "),
      userSignerNames: signatureRequest.matterSignatureRequestUserSigners
        .map((signer) => displaySignatureRequestUserSignerName(signer))
        .filter(Boolean)
        .join(" "),
    }));

    const fuse = new Fuse(searchable, {
      keys: [
        { name: "matterFileName", weight: 0.6 },
        { name: "statusLabel", weight: 0.2 },
        { name: "contactSignerNames", weight: 0.1 },
        { name: "userSignerNames", weight: 0.1 },
      ],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 1,
      includeScore: false,
    });

    return fuse
      .search(sanitizedQuery)
      .map((result) => result.item.signatureRequest);
  }, [activeTab, query, signatureRequestData]);

  const signatureRequestCount = matter.signatureRequests.length;
  const signatureRequestCounts = useMemo(() => {
    return signatureRequestData.reduce<
      Record<SignatureRequestFilterTab, number>
    >(
      (counts, signatureRequest) => {
        const tab = getStatusTab(signatureRequest);
        counts[tab] += 1;
        counts.all += 1;
        return counts;
      },
      {
        outstanding: 0,
        expired: 0,
        completed: 0,
        all: 0,
      },
    );
  }, [signatureRequestData]);

  return (
    <div className="flex min-h-0 flex-col gap-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Text variant="title">
          {hasViewPermission
            ? `Signature requests (${signatureRequestCount})`
            : "Signature requests"}
        </Text>
      </div>

      {hasViewPermission ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Tabs
            value={activeTab}
            onValueChange={(value) => {
              setActiveTab(value as SignatureRequestFilterTab);
            }}
            className="min-w-0 flex-1"
          >
            <TabsList className="w-fit items-center justify-start">
              <TabsTrigger value="all">
                All ({signatureRequestCounts.all})
              </TabsTrigger>
              <TabsTrigger value="outstanding">
                Outstanding ({signatureRequestCounts.outstanding})
              </TabsTrigger>
              <TabsTrigger value="expired">
                Expired ({signatureRequestCounts.expired})
              </TabsTrigger>
              <TabsTrigger value="completed">
                Completed ({signatureRequestCounts.completed})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="all" />
            <TabsContent value="outstanding" />
            <TabsContent value="expired" />
            <TabsContent value="completed" />
          </Tabs>
          <div className="flex w-full max-w-[520px] items-center gap-3">
            <div className="min-w-0 flex-1">
              <Input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
                placeholder="Search file, signer, status"
              />
            </div>
            {hasCreatePermission && (
              <Button
                onClick={() => {
                  openCreateSignatureRequestModal({
                    matterId: matter.id,
                  });
                }}
                className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
              >
                <FileSignatureRequestIcon className="h-4 w-4" />
                Create signature request
              </Button>
            )}
          </div>
        </div>
      ) : null}

      {!hasViewPermission && (
        <Card className="w-full border-2">
          <CardHeader className="space-y-1">
            <Text variant="subHeading">
              You do not have permission to view signature requests.
            </Text>
          </CardHeader>
        </Card>
      )}
      {hasViewPermission && (
        <>
          {signatureRequestData.length === 0 ? (
            <Card className="w-full border-2 border-dashed">
              <CardHeader className="space-y-1">
                <Text
                  variant="body"
                  className="text-muted-foreground text-center"
                >
                  {"You haven't added a signature request yet."}
                </Text>
                <Text variant="subHeading" className="text-center">
                  {"Create your first by clicking 'Create signature request'"}
                </Text>
              </CardHeader>
            </Card>
          ) : visibleRows.length === 0 ? (
            <Card className="w-full border-2 border-dashed">
              <CardHeader className="space-y-1">
                <Text
                  variant="body"
                  className="text-muted-foreground text-center"
                >
                  No results found.
                </Text>
                <Text variant="subHeading" className="text-center">
                  Try a different search term or tab.
                </Text>
              </CardHeader>
            </Card>
          ) : (
            <div className="flex flex-col gap-y-4">
              {visibleRows.map((signatureRequest) => (
                <MatterSignatureRequestItem
                  key={signatureRequest.matterSignatureRequestId}
                  matter={matter}
                  signatureRequest={signatureRequest}
                  signatureRequestPresentation="modal"
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
